/**
 * Modelo del mapa de sanidad — derivación PURA (sin red, sin React), separada
 * del hook para poder testearla en Node sin instanciar el cliente Supabase.
 * Documentación de la semántica: plan Fase 5 (T-A 2026-08-03).
 */
import { calculatePolygonArea } from "@/greenhouse-kernel/lib/geometry/polygon";
import { normalizeDeg, pivotOf, rotatedPlaneBox, rotatedSize } from "./ghTransform";
import {
  greenhouseOffsetInPlane,
  ringFromGeoPolygon,
  type LatLng,
  type XY,
} from "@/lib/geo/wgs84Local";
import { buildPestCanon, samePest, type RawPest } from "./pestCanon";
import { saidVerdict } from "@/lib/pests/sessionReport";

/**
 * EL ESTADO ES EL CICLO DE VIDA DEL PARTE, Y NADA MÁS (31-08-2026).
 *
 * Antes esta lista mezclaba DOS variables en un solo canal. `outbreak_high` vs
 * `outbreak` era la GRAVEDAD; `reported` vs `in_treatment` vs `recovered` era
 * el ciclo de vida. Al mirar un punto no se podía saber a cuál de las dos
 * preguntas estaba contestando su color, y el resultado era que no contestaba a
 * ninguna. La gravedad se va al TAMAÑO del pin (`Focus.severity`, que ya
 * estaba), y el color se queda con una sola cosa que decir.
 *
 * Leído como «¿de quién es la pelota?»:
 *
 *   open      ROJO      nadie ha hecho nada — es tuya
 *   planned   AZUL      hay tratamiento previsto, sin ejecutar — está en cola
 *   treated   VERDE     tratamiento hecho, o el trabajador lo da por resuelto
 *   resolved  APAGADO   validado y cerrado — de nadie
 *
 * Y SE FUE `treated_block`. Era «hubo una pulverización en este sector dentro de
 * 21 días», sin mirar para qué plaga: en una finca que pulveriza por calendario
 * eso es siempre cierto y pintaba de tratado el 77 % de los partes abiertos.
 * Una pulverización que NO se enlazó no cambia el estado; se cuenta aparte, en
 * `Focus.sprayedNearby`, y el mapa la usa para atenuar el rojo sin dejar de
 * decir que el parte sigue abierto. Ver `pestCanon.ts`.
 */
export type LineState =
  | "open"
  | "planned"
  | "treated"
  | "resolved"
  | "no_data";

export interface HealthRange {
  from: string; // ISO date (inclusive)
  to: string;
}

export interface GhBlock {
  id: string;
  name: string;
  points: XY[];
  centroid: XY;
}
export interface GhRow {
  id: string;
  blockId: string;
  lineNumber: number;
  a: XY;
  b: XY;
  /** Trozos de una fila física partida (muesca cóncava, pasillo). Ausente ⇒ la
   *  fila es un solo tramo y `a`/`b` la describen entera, como siempre. */
  facetas?: { a: XY; b: XY }[];
  lengthM: number;
}
export interface GhModel {
  id: string;
  name: string;
  offset: XY; // posición dentro del plano común de la ubicación
  /* Giro de la nave en el plano, en grados horarios de pantalla. UNO SOLO para
     las dos orientaciones, a diferencia del desplazamiento: la colocación es una
     negociación con la forma de la pantalla —en el teléfono se apilan, en el
     ordenador se reparten—, pero el ángulo es una afirmación sobre el MUNDO
     («la Nave 4 va de nordeste a sudoeste»). Dos vistas de la misma finca que no
     se pusieran de acuerdo en hacia dónde apunta un edificio estarían enseñando
     dos fincas distintas. */
  rotationDeg: number;
  /** Centro de la caja SIN GIRAR, en local. El único punto que el giro no mueve. */
  pivot: XY;
  perimeter: XY[];
  /** Huecos sin planta dentro de la nave (pasillos dibujados en el asistente),
   *  en el MISMO espacio local que `perimeter`. Vacío en las naves antiguas y en
   *  las importadas: nunca es null, para que quien dibuja no tenga que
   *  preguntar. */
  pasillos: XY[][];
  bbox: { minX: number; minY: number; maxX: number; maxY: number; width: number; height: number };
  areaM2: number;
  blocks: GhBlock[];
  rows: GhRow[];
  /**
   * DÓNDE ESTÁ ESTA NAVE EN LA TIERRA. `null` si se dibujó sin anclaje
   * geográfico (hoy no pasa en producción: las 13 con plano lo tienen, porque
   * todas salieron del asistente, que dibuja sobre satélite).
   *
   * El anillo es el MISMO perímetro que `perimeter`, vértice a vértice, pero
   * sin proyectar. Es lo que permite poner el plano encima de la foto: con el
   * centroide y el anillo, `localToWgs84` sabe a qué latitud y longitud cae
   * cada metro local.
   *
   * Va aquí y no se vuelve a leer de la fila cruda porque el modelo es lo único
   * que llega a las capas de dibujo, y calcularlo dos veces es la forma
   * habitual de que un día las dos cuentas dejen de coincidir.
   */
  geo: { centroid: LatLng; ringWgs84: LatLng[] } | null;
  /**
   * RETOQUE SOBRE LA FOTO: unos metros y unos grados para cuadrar el plano con
   * el tejado de la imagen, cuando el dibujo original no acabó de caer encima.
   *
   * Deliberadamente APARTE de `healthMapOffset`: aquel coloca la nave dentro de
   * un plano inventado y sigue mandando en «ver todas», donde no hay foto con la
   * que cuadrar nada. Mezclarlos movería la nave dos veces.
   *
   * En metros y grados LOCALES, la misma unidad que la geometría, para que se
   * pueda aplicar con la maquinaria de giro que ya existe.
   */
  satelliteAdjust: { dx: number; dy: number; rot: number } | null;
}
/**
 * El retoque de satélite, validado. Basura → `null`, nunca un `NaN`.
 *
 * Un `NaN` aquí no da error: viaja hasta el `transform` del SVG y hace
 * desaparecer la nave del plano sin que nada lo diga. Es exactamente el mismo
 * cuidado que se le tiene al desplazamiento manual unas líneas más abajo.
 */
function readAdjust(geometry: unknown): { dx: number; dy: number; rot: number } | null {
  const a = (geometry as { satelliteAdjust?: unknown } | null)?.satelliteAdjust as
    | { dx?: unknown; dy?: unknown; rot?: unknown }
    | undefined;
  if (!a || typeof a !== "object") return null;
  const dx = typeof a.dx === "number" && Number.isFinite(a.dx) ? a.dx : 0;
  const dy = typeof a.dy === "number" && Number.isFinite(a.dy) ? a.dy : 0;
  const rot = typeof a.rot === "number" && Number.isFinite(a.rot) ? a.rot : 0;
  if (dx === 0 && dy === 0 && rot === 0) return null;
  return { dx, dy, rot };
}

export interface Focus {
  id: string;
  ghId: string;
  blockId: string;
  rowId: string | null;
  /** Coordenadas LOCALES de la nave (el render suma gh.offset). */
  x: number;
  y: number;
  pestName: string | null;
  /** La clave canónica de la plaga (ver `pestCanon`). Agrupa donde el nombre no. */
  pestKey: string | null;
  severity: string | null;
  date: string;
  state: LineState;
  approx: boolean;
  /** Precisión GPS del último reporte (m); >50 ⇒ "ubicación aproximada". */
  accuracyM: number | null;
  /** Los partes que hay detrás de esta chincheta, del más antiguo al más
      reciente. Uno solo si el parte va suelto; todos los del foco si está
      enlazado (la «futura agrupación explícita» para la que se guardó la lista
      llegó el 21-09-2026). */
  sessionIds: string[];
  /**
   * QUÉ ES ESTA CHINCHETA.
   *  · `focus`    — un foco de verdad (`pest_foci`): varios muestreos confirmados
   *                 de la misma plaga en el mismo sitio. `id` es el del foco.
   *  · `implicit` — un parte confirmado que nadie ha enlazado (los de antes de
   *                 los focos, o los que clasifica un bundle viejo). Se pinta
   *                 igual que siempre: él solo. `id` es el del parte.
   *  · `loose`    — un parte aún SIN confirmar. Nunca pertenece a un foco.
   * Ausente ⇒ `implicit`: quien construya un `Focus` a mano no tiene por qué saberlo.
   */
  kind?: "focus" | "implicit" | "loose";
  focusId?: string | null;
  /** Cuántos MUESTREOS lleva (las revisiones limpias no cuentan). Ausente ⇒ 1. */
  count?: number;
  firstDate?: string;
  /** Cada parte del foco con SU estado: la ficha los enseña uno a uno, y así un
      muestreo ya tratado nunca queda tapado por otro sin tratar. */
  samples?: FocusSample[];
  /** Avance frente al muestreo anterior. `null` = primer muestreo, o no se sabe. */
  evolution?: FocusEvolution | null;
  /** Se resolvió y ha vuelto dentro de la ventana de rebrote. */
  rebound?: boolean;
  /** Cuántos muestreos hay en cada estado. */
  stateCounts?: Partial<Record<LineState, number>>;
  /**
   * LA PULVERIZACIÓN QUE NO ESTÁ ENLAZADA PERO SÍ VA DE LO MISMO.
   *
   * Misma plaga (canónica), mismo sector, después del parte y dentro de la
   * ventana. NO cambia el estado —el parte sigue `open`, porque nadie lo ha
   * cerrado— pero es información real: algo se hizo aquí, y hace poco. El mapa
   * la usa para atenuar el rojo y para poner el pin del tratamiento al lado.
   *
   * `null` cuando no hay ninguna, que es el caso normal: solo 4 de los 22
   * partes abiertos de producción tienen una.
   */
  sprayedNearby: {
    sprayId: string;
    date: string;
    product: string | null;
    /** Días entre esa aplicación y HOY. Lo calcula el modelo, que es quien
        tiene la fecha de hoy; el dibujo solo la lee. */
    daysAgo: number;
  } | null;
}

/** `unknown` = quien muestreó dijo «No lo sé» (24-09-2026): se enseña así, sin marca ▲▬▼. */
export type FocusEvolution = "worse" | "same" | "better" | "gone" | "unknown";

export interface FocusSample {
  sessionId: string;
  /** Un muestreo con plaga, o una revisión limpia («ya no hay»). */
  kind: "sampling" | "clean_check";
  date: string;
  state: LineState;
  severity: string | null;
  /** Lo que dijo quien estaba delante de la planta, si dijo algo. */
  verdict: FocusEvolution | null;
  approx: boolean;
}

/**
 * QUÉ ABRE TOCAR UNA CHINCHETA (Pablo, 25-09-2026: «no era un foco, es simplemente una
 * sesión»). Un foco con UN solo parte detrás —ni otro muestreo, ni seguimiento, ni revisión
 * limpia— abre ESE parte: su ficha dice a qué foco pertenece y lleva a él con «Abrir foco»
 * (F28), donde siguen tratar, pedir seguimiento y cerrar. Con dos o más, la ficha del foco.
 * Un parte suelto o de antes de los focos abre el más reciente, como siempre.
 */
export function pinTarget(
  f: { kind?: Focus["kind"]; focusId?: string | null; sessionIds: readonly string[] },
): { focusId: string } | { sessionId: string } | null {
  if (f.kind === "focus" && f.focusId && f.sessionIds.length > 1) return { focusId: f.focusId };
  const sid = f.sessionIds[f.sessionIds.length - 1];
  if (sid) return { sessionId: sid };
  return f.kind === "focus" && f.focusId ? { focusId: f.focusId } : null;
}

/**
 * EL AVANCE, en un solo sitio para que el mapa y la ficha no puedan discrepar.
 *
 * Manda el veredicto de quien muestreó —estaba delante de la planta—; si no lo
 * dio, se comparan las gravedades de los dos últimos muestreos. Sin anterior, o
 * sin gravedad en alguno, no hay avance que enseñar: `null`, nunca un «igual»
 * inventado. Si dijo «No lo sé» (`unknown`) eso es lo que se enseña: tampoco se
 * rellena con las gravedades (24-09-2026).
 */
export function deriveEvolution(
  prev: { severity: string | null } | null,
  cur: { severity: string | null; verdict?: FocusEvolution | null },
): FocusEvolution | null {
  if (cur.verdict) return cur.verdict;
  if (!prev) return null;
  const a = SEV_RANK[prev.severity ?? ""] ?? 0;
  const b = SEV_RANK[cur.severity ?? ""] ?? 0;
  if (a === 0 || b === 0) return null;
  return b > a ? "worse" : b < a ? "better" : "same";
}

/** Hasta dónde se atenúa un parte abierto con pulverización cerca. */
export const FADE_FLOOR = 0.45;

/**
 * CUÁNTO SE PINTA UN FOCO, de 0 a 1.
 *
 * Tres casos:
 *  · Resuelto y validado: a media tinta. Sigue ahí —su línea también— pero no
 *    compite con lo que queda por hacer.
 *  · Abierto CON una pulverización de su plaga al lado: rojo que se va
 *    aclarando según pasan los días desde esa aplicación (petición del usuario
 *    31-08-2026: «seguirá en rojo, pero irá más claro conforme pasen los
 *    días»). Sigue siendo rojo porque nadie ha cerrado el parte; lo que dice el
 *    tono es que algo se hizo aquí.
 *  · Todo lo demás: entero.
 *
 * CON SUELO, y no es un detalle. Un desvanecido que llega a cero hace
 * desaparecer del mapa un parte que NADIE ha cerrado — que es la misma mentira
 * que veníamos a quitar, solo que más discreta. Por debajo de FADE_FLOOR no
 * baja nunca.
 *
 * (Si algún día se quiere lo contrario —más claro justo después de tratar y
 * volviendo a rojo pleno según se agota la ventana sin que nadie lo confirme,
 * que es la lectura de resistencias— es invertir esta única cuenta.)
 */
export function focusOpacity(f: Pick<Focus, "state" | "sprayedNearby">): number {
  if (f.state === "resolved") return 0.5;
  if (f.state !== "open" || !f.sprayedNearby) return 1;
  const t = Math.min(1, Math.max(0, f.sprayedNearby.daysAgo / TREATED_BLOCK_DAYS));
  return 1 - (1 - FADE_FLOOR) * t;
}
export interface PestAgg {
  pestName: string | null;
  severity: string | null;
  lastDate: string;
  reports: number;
}
export interface LineHealth {
  state: LineState;
  weight: number; // 0..1 — intensidad (severidad × frescura × reportes)
  pests: PestAgg[];
}
export interface BlockHealth {
  totalLines: number;
  affectedLines: number;
  state: LineState;
  pressure: number; // 0..1
  dominantPest: PestAgg | null;
  phi: { until: string; product: string } | null; // solo si activo a fecha `to`
  irm: { status: "warning" | "over"; detail: string } | null;
  lastTreatment: { product: string; date: string; phiUntil: string | null } | null;
  /**
   * DÍAS DESDE LA ÚLTIMA PULVERIZACIÓN DEL SECTOR, o `null` si no hay ninguna
   * dentro de la ventana.
   *
   * Es un hecho de ÁREA, y por eso se cuenta aquí y no en los partes: «en este
   * sector se ha echado algo hace poco» es verdad del sector entero, no de un
   * punto suyo. Se dibuja tiñendo el bloque de verde (petición del usuario
   * 31-08-2026: «que ponga el bloque entero verde»), que además resuelve el
   * problema de dónde plantar una chincheta de tratamiento: en ningún sitio,
   * porque una pulverización no ocurre en un punto.
   *
   * NO mira la plaga a propósito, al revés que `Focus.sprayedNearby`. Aquí no
   * se está diciendo que ningún parte esté tratado —eso sigue exigiendo enlace—
   * sino solo que el sector se ha pulverizado. Son dos afirmaciones distintas
   * en dos canales distintos: el área y la chincheta.
   */
  treatedDaysAgo: number | null;
}
/**
 * UNA PULVERIZACIÓN, PUESTA EN EL MAPA.
 *
 * Va al lado de los partes de su sector porque es la otra mitad de la historia:
 * el parte dice «hay araña roja aquí» y esto dice «y el 27 se echó Vertimec».
 * Antes solo existía como una deducción invisible que teñía el parte de azul.
 *
 * EN EL CENTROIDE DEL SECTOR, y no hay nada mejor: de las 722 pulverizaciones de
 * producción, CERO llevan línea y 9 llevan rango. Se apunta el sector y ya. Si
 * algún día el parte de aplicación captura la línea —la columna
 * `spray_logs.block_row_id` existe y no la escribe nadie—, esto cae solo en su
 * sitio de verdad.
 */
export interface TreatmentMark {
  /** id del `spray_logs`. */
  id: string;
  ghId: string;
  blockId: string;
  /** Centroide del sector, en local de la nave. */
  x: number;
  y: number;
  date: string;
  /** Días hasta hoy — para ordenar y para el rótulo. */
  daysAgo: number;
  products: string[];
  /** Plagas a las que iba dirigida (las que traen nombre), para enseñar. */
  pests: string[];
  /**
   * Las mismas, CANONIZADAS, para comparar (ver `pestCanon`).
   *
   * Van aquí y no se recalculan fuera porque el catálogo de plagas vive en la
   * consulta y no llega al modelo: quien quiera cruzar —hoy, la hoja que ofrece
   * enlazar el tratamiento a un parte— necesita las claves ya resueltas o
   * acabaría comparando nombres a pelo, que es el fallo original.
   */
  pestKeys: string[];
  /** La tarea de la que salió, si salió de una. Es lo que se enlaza. */
  taskId: string | null;
  /**
   * Explica algún parte abierto de su sector: misma plaga y dentro de ventana.
   * Es el que está atenuando un rojo, así que se dibuja entero; los demás son
   * calendario y van más discretos.
   */
  explains: boolean;
}

export interface GhStats {
  sectors: number;
  lines: number;
  activeFoci: number;
  worstSeverity: string | null;
  blocksInPhi: number;
}
export interface FarmHealthModel {
  bounds: { minX: number; minY: number; maxX: number; maxY: number; width: number; height: number };
  greenhouses: GhModel[];
  foci: Focus[];
  lineHealth: Map<string, LineHealth>;
  blockHealth: Map<string, BlockHealth>;
  /** Las pulverizaciones del rango, para pintarlas junto a los partes. */
  treatments: TreatmentMark[];
  ghStats: Map<string, GhStats>;
  summary: { areaHa: number; greenhouses: number; sectors: number; lines: number };
}

// ── Entradas crudas (shape mínimo de las queries) ────────────────────────────
export interface RawGreenhouse {
  id: string;
  name: string;
  geometry: unknown;
  centroid_lat: number | null;
  centroid_lng: number | null;
}
export interface RawBlock {
  id: string;
  greenhouse_id: string;
  name: string;
  geometry: unknown;
  archived_at: string | null;
}
export interface RawRow {
  id: string;
  block_id: string;
  line_number: number;
  geometry: unknown;
  length_m: number | null;
  is_active: boolean;
}
export interface RawSession {
  id: string;
  greenhouse_id: string | null;
  block_id: string | null;
  block_row_id: string | null;
  date: string;
  severity: string | null;
  triage_status: string;
  triage_pest_name_snapshot: string | null;
  /** Preferido sobre el nombre para saber QUÉ plaga es (ver `pestCanon`). */
  triage_pest_id?: string | null;
  review_status: string;
  linked_task_id: string | null;
  /** El worker declaró resuelto (pendiente de validación del manager). */
  worker_resolved_at?: string | null;
  notes: string | null;
  /** T-B: ancla real capturada al reportar (null en histórico legacy). */
  anchor_x?: number | string | null;
  anchor_y?: number | string | null;
  anchor_source?: string | null;
  gps_accuracy_m?: number | null;
  /* ── Focos persistentes (21-09-2026). TODO opcional: los bundles viejos y las
     bases sin migrar no los traen, y el mapa tiene que pintarse igual que hoy. */
  focus_id?: string | null;
  dismissal_reason?: string | null;
  evolution_verdict?: string | null;
  /* Para saber si dijo «No lo sé» (`saidVerdict`): 24-09-2026. */
  evolution_verdict_by?: string | null;
  followup_answer?: string | null;
  resolved_at?: string | null;
  triaged_at?: string | null;
  created_at?: string | null;
}
/** Un foco de `pest_foci`, con lo justo para pintarlo. */
export interface RawFocus {
  id: string;
  block_id: string;
  pest_id?: string | null;
  pest_name_snapshot?: string | null;
  reopen_count?: number | null;
  last_reopened_at?: string | null;
}
export interface RawObservation {
  scouting_session_id: string;
  observed_row_start: number | null;
  pest_name_snapshot: string | null;
  severity: string | null;
}
export interface RawSpray {
  id: string;
  block_id: string | null;
  date: string;
  /** Rango de líneas tratadas (line_number inclusive); null = bloque entero. */
  row_start?: number | null;
  row_end?: number | null;
  /* `pest_id` / `pest_name_snapshot`: PARA QUÉ era esta línea de la mezcla. Es
     lo que decide si la pulverización explica un parte o solo coincidió en el
     calendario. Relleno en el 85 % de las líneas de producción. */
  spray_log_entries: {
    product_name_snapshot: string | null;
    phi_days_applied: number | null;
    pest_id?: string | null;
    pest_name_snapshot?: string | null;
  }[];
  /** La tarea que la generó (713 de 722 en producción la tienen). */
  task_id?: string | null;
}

/**
 * La tarea enlazada a un parte, solo con lo que decide el color.
 *
 * El estado sale de la TAREA y no de `review_status` de la sesión. Antes se
 * deducía de `in_review`/`in_progress`, que es la revisión del MANAGER y no lo
 * que ha pasado con el tratamiento: una tarea completada seguía leyéndose
 * «en tratamiento» hasta que alguien tocara la sesión.
 */
export interface RawTask {
  id: string;
  /** pending · in_progress · completed · cancelled */
  status: string | null;
  /** Para el histórico: cuándo se planificó y cuándo se completó. */
  created_at?: string | null;
  completed_at?: string | null;
}
export interface RawIrmRow {
  block_id: string;
  product_name: string;
  applications_used: number;
  max_applications: number;
  status: string;
}
/**
 * Qué colocación de naves se usa. Hay DOS guardadas por nave, porque una
 * pantalla apaisada y una vertical no admiten la misma disposición: colocar las
 * naves en el teléfono dejaba el ordenador con todo diminuto, y al revés
 * (informe del usuario 25-08-2026).
 *
 * `landscape` es la histórica (`geometry.healthMapOffset`), así que las
 * colocaciones que ya existen NO se mueven el día del despliegue.
 */
export type LayoutVariant = "landscape" | "portrait";

export interface FarmHealthInput {
  greenhouses: RawGreenhouse[];
  blocks: RawBlock[];
  rows: RawRow[];
  sessions: RawSession[];
  observations: RawObservation[];
  sprays: RawSpray[];
  irm: RawIrmRow[];
  /** Catálogo de plagas: solo para canonizar nombres (ver `pestCanon`). */
  pests?: RawPest[];
  /** Las tareas enlazadas desde los partes de este lote. */
  tasks?: RawTask[];
  /** Los focos de estas naves. Sin ellos, cada parte es su propia chincheta. */
  foci?: RawFocus[];
  /** Por defecto, la apaisada: es la que había antes de que hubiera dos. */
  layoutVariant?: LayoutVariant;
}

const SEV_RANK: Record<string, number> = { low: 1, medium: 2, high: 3, critical: 4 };
const SEV_WEIGHT: Record<string, number> = { low: 0.35, medium: 0.6, high: 0.85, critical: 1 };
const GH_GAP_M = 15;

/** Los pasillos guardados por el asistente, saneados: solo anillos dibujables.
 *  Una nave publicada antes de que existieran no trae la clave — de ahí el `?? []`
 *  en vez de un fallo. */
const pasillosOf = (g: unknown): XY[][] =>
  ((g as { pasillos?: XY[][] } | null)?.pasillos ?? []).filter(
    (ring) => Array.isArray(ring) && ring.length >= 3,
  );

const perimeterOf = (g: unknown): XY[] =>
  ((g as { perimeter?: XY[] } | null)?.perimeter ?? []).filter(
    (p) => typeof p?.x === "number" && typeof p?.y === "number",
  );
const pointsOf = (g: unknown): XY[] =>
  ((g as { points?: XY[] } | null)?.points ?? []).filter(
    (p) => typeof p?.x === "number" && typeof p?.y === "number",
  );
/** Las facetas guardadas, si las hay. Una fila partida por una muesca cóncava o
 *  por un pasillo llega como varios tramos bajo el mismo número; sin esto el mapa
 *  solo pintaba el primero y el resto de la nave se veía SIN LÍNEAS. */
const facetasOf = (geometry: unknown): { a: XY; b: XY }[] | undefined => {
  const f = (geometry as { facetas?: unknown } | null)?.facetas;
  if (!Array.isArray(f) || f.length < 2) return undefined;
  const out: { a: XY; b: XY }[] = [];
  for (const trozo of f) {
    const s = segmentOf(trozo);
    if (s) out.push(s);
  }
  return out.length >= 2 ? out : undefined;
};

const segmentOf = (g: unknown): { a: XY; b: XY } | null => {
  const seg = g as { a?: XY; b?: XY } | null;
  return seg?.a && seg?.b ? { a: seg.a, b: seg.b } : null;
};
const bboxOf = (pts: XY[]) => {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of pts) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
};
const centroidOf = (pts: XY[]): XY => {
  let x = 0, y = 0;
  for (const p of pts) { x += p.x; y += p.y; }
  const n = Math.max(1, pts.length);
  return { x: x / n, y: y / n };
};
/** Cuántos días después del parte puede una pulverización seguir hablando de
    él. Los mismos 21 que usan los informes para dar un tratamiento por
    relacionado. Ya NO decide un estado —eso lo hace el enlace— sino solo si la
    aplicación entra en `Focus.sprayedNearby`. */
export const TREATED_BLOCK_DAYS = 21;

const daysBetween = (a: string, b: string) =>
  Math.max(0, (new Date(b).getTime() - new Date(a).getTime()) / 86_400_000);
const addDays = (iso: string, days: number) => {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

/** Línea legacy de una sesión: notes "Línea: N" (cualquier idioma razonable). */
export const parseLegacyLine = (notes: string | null): number | null => {
  if (!notes) return null;
  const m = notes.match(/l[ií]n(?:ea|e)?a?\s*:?\s*(\d{1,3})/i);
  return m ? Number(m[1]) : null;
};

/** De más a menos reclamo de atención: sin tratar manda sobre todo lo demás. */
export const worseState = (a: LineState, b: LineState): LineState => {
  const order: LineState[] = ["open", "planned", "treated", "resolved", "no_data"];
  return order.indexOf(a) <= order.indexOf(b) ? a : b;
};

/**
 * DÓNDE ESTÁ CADA NAVE, y nada más: geometría, colocación en el plano, encuadre.
 *
 * Partido de la sanidad (21-09-2026) por la barra del histórico: al arrastrarla
 * se recalcula el estado de los focos decenas de veces, y la colocación no
 * depende de la fecha. Calculada una vez, las naves conservan su identidad de
 * objeto entre un día y el siguiente, y las capas estáticas del mapa —suelo,
 * pasillos, textura, rótulos— no se vuelven a pintar.
 */
export interface FarmLayout {
  greenhouses: GhModel[];
  bounds: FarmHealthModel["bounds"];
  summary: FarmHealthModel["summary"];
}

export function buildFarmLayout(input: Pick<FarmHealthInput, "greenhouses" | "blocks" | "rows" | "layoutVariant">): FarmLayout {
  const layoutVariant: LayoutVariant = input.layoutVariant ?? "landscape";
  // ── Colocación de naves en el plano común ─────────────────────────────
  const ghRaw = input.greenhouses
    .map((g) => ({ raw: g, perimeter: perimeterOf(g.geometry) }))
    .filter((g) => g.perimeter.length >= 3);

  const rings = new Map<string, LatLng[]>();
  const centers: LatLng[] = [];
  for (const g of ghRaw) {
    const ring = ringFromGeoPolygon((g.raw.geometry as { geoPolygonWgs84?: unknown })?.geoPolygonWgs84);
    if (ring.length >= 3 && g.raw.centroid_lat != null && g.raw.centroid_lng != null) {
      rings.set(g.raw.id, ring);
      centers.push({ lat: g.raw.centroid_lat, lng: g.raw.centroid_lng });
    }
  }
  const commonCenter: LatLng | null = centers.length
    ? {
        lat: centers.reduce((s, c) => s + c.lat, 0) / centers.length,
        lng: centers.reduce((s, c) => s + c.lng, 0) / centers.length,
      }
    : null;

  // ── Colocación: manual (healthMapOffset, colocada por el usuario en el modo
  // edición) > geográfica (si las naves están razonablemente cerca) > empaquetado
  // compacto en estanterías. La geográfica con parcelas muy separadas producía
  // un mapa diminuto y desperdigado (QA usuario 2026-08-03).
  const prelim = ghRaw.map((g) => {
    const bbox = bboxOf(g.perimeter);
    /* Si la variante activa no tiene colocación propia, se toma PRESTADA la
       otra en vez de caer al empaquetado automático: el automático no sabe de
       orientación (apila en estanterías de dos naves de ancho pase lo que
       pase), así que recolocaría de golpe la única finca que de verdad usa
       esto. El préstamo es de solo lectura: nunca se escribe de vuelta. */
    const geom = g.raw.geometry as {
      healthMapOffset?: XY;
      healthMapOffsetPortrait?: XY;
      healthMapRotationDeg?: number;
    } | null;
    const own = layoutVariant === "portrait" ? geom?.healthMapOffsetPortrait : geom?.healthMapOffset;
    const other = layoutVariant === "portrait" ? geom?.healthMapOffset : geom?.healthMapOffsetPortrait;
    const manual = own ?? other ?? null;
    const ring = rings.get(g.raw.id);
    const geo = ring && commonCenter ? greenhouseOffsetInPlane(ring, commonCenter) : null;
    /* Las DOS coordenadas, y finitas. `typeof manual.x === "number"` dejaba pasar
       un `{x: 5}` heredado —`offset.y` sale `undefined` y todo el plano se va a
       `NaN`— y también un `NaN`, que es de tipo number. */
    const okOffset =
      manual != null && Number.isFinite(manual.x) && Number.isFinite(manual.y) ? manual : null;
    const rotationDeg = normalizeDeg(geom?.healthMapRotationDeg);
    const pivot = pivotOf(g.perimeter);
    return { g, bbox, manual: okOffset, geo, rotationDeg, pivot };
  });
  const geoPlaced = prelim.filter((p) => !p.manual && p.geo);
  let geoOk = geoPlaced.length > 0;
  if (geoOk) {
    const minX = Math.min(...geoPlaced.map((p) => p.geo!.x + p.bbox.minX));
    const maxX = Math.max(...geoPlaced.map((p) => p.geo!.x + p.bbox.maxX));
    const minY = Math.min(...geoPlaced.map((p) => p.geo!.y + p.bbox.minY));
    const maxY = Math.max(...geoPlaced.map((p) => p.geo!.y + p.bbox.maxY));
    const sumW = geoPlaced.reduce((acc, p) => acc + p.bbox.width, 0);
    const sumH = geoPlaced.reduce((acc, p) => acc + p.bbox.height, 0);
    // Disperso = el span geográfico dobla con creces lo que ocupan las naves.
    geoOk =
      maxX - minX <= Math.max(2.2 * sumW, sumW + 120) &&
      maxY - minY <= Math.max(2.2 * sumH, sumH + 120);
  }
  // Empaquetado en estanterías (para las que no tengan manual ni geo utilizable).
  let shelfX = 0;
  let shelfY = 0;
  let shelfRowH = 0;
  const shelfMaxW = Math.max(...prelim.map((p) => p.bbox.width)) * 2 + GH_GAP_M;
  const packNext = (bbox: ReturnType<typeof bboxOf>): XY => {
    if (shelfX > 0 && shelfX + bbox.width > shelfMaxW) {
      shelfX = 0;
      shelfY += shelfRowH + GH_GAP_M;
      shelfRowH = 0;
    }
    const off = { x: shelfX - bbox.minX, y: shelfY - bbox.minY };
    shelfX += bbox.width + GH_GAP_M;
    shelfRowH = Math.max(shelfRowH, bbox.height);
    return off;
  };
  const greenhouses: GhModel[] = prelim.map(({ g, bbox, manual, geo, rotationDeg, pivot }) => {
    /* El empaquetado apila la huella GIRADA, no la de dibujo: una nave girada 90°
       mide de ancho lo que medía de alto, y con la caja sin girar se solaparía
       con su vecina. `rotatedSize` mide el perímetro real, no las esquinas de la
       caja, que a 45° se pasan hasta un 41 %. */
    const packBox = rotationDeg
      ? (() => {
          const s = rotatedSize({ offset: { x: 0, y: 0 }, rotationDeg, pivot }, g.perimeter);
          return { ...bbox, width: s.w, height: s.h };
        })()
      : bbox;
    const offset: XY = manual ?? (geoOk && geo ? geo : packNext(packBox));
    const blocks: GhBlock[] = input.blocks
      .filter((b) => b.greenhouse_id === g.raw.id && !b.archived_at)
      .map((b) => ({ id: b.id, name: b.name, points: pointsOf(b.geometry), centroid: { x: 0, y: 0 } }))
      .filter((b) => b.points.length >= 3)
      .map((b) => ({ ...b, centroid: centroidOf(b.points) }));
    const blockIds = new Set(blocks.map((b) => b.id));
    const rows: GhRow[] = input.rows
      .filter((r) => blockIds.has(r.block_id) && r.is_active)
      .map((r): GhRow | null => {
        const seg = segmentOf(r.geometry);
        return seg
          ? {
              id: r.id, blockId: r.block_id, lineNumber: r.line_number,
              a: seg.a, b: seg.b,
              facetas: facetasOf(r.geometry),
              lengthM: r.length_m ?? Math.hypot(seg.b.x - seg.a.x, seg.b.y - seg.a.y),
            }
          : null;
      })
      .filter((r): r is GhRow => r !== null);
    return {
      id: g.raw.id,
      name: g.raw.name,
      offset,
      rotationDeg,
      pivot,
      perimeter: g.perimeter,
      pasillos: pasillosOf(g.raw.geometry),
      bbox,
      areaM2: calculatePolygonArea(g.perimeter),
      blocks,
      rows,
      geo:
        rings.has(g.raw.id) && g.raw.centroid_lat != null && g.raw.centroid_lng != null
          ? {
              centroid: { lat: g.raw.centroid_lat, lng: g.raw.centroid_lng },
              ringWgs84: rings.get(g.raw.id)!,
            }
          : null,
      satelliteAdjust: readAdjust(g.raw.geometry),
    };
  });

  /* La huella de cada nave YA GIRADA: es lo que decide el encuadre automático y
     la barra de escala. Con la caja sin girar, una nave a 45° se saldría del
     encuadre por las esquinas. El camino sin giro es la resta de siempre. */
  const planeBoxes = greenhouses.map((g) =>
    g.rotationDeg
      ? rotatedPlaneBox({ offset: g.offset, rotationDeg: g.rotationDeg, pivot: g.pivot }, g.perimeter)
      : {
          minX: g.offset.x + g.bbox.minX,
          minY: g.offset.y + g.bbox.minY,
          maxX: g.offset.x + g.bbox.maxX,
          maxY: g.offset.y + g.bbox.maxY,
        },
  );
  const bounds = planeBoxes.length
    ? {
        minX: Math.min(...planeBoxes.map((b) => b.minX)),
        minY: Math.min(...planeBoxes.map((b) => b.minY)),
        maxX: Math.max(...planeBoxes.map((b) => b.maxX)),
        maxY: Math.max(...planeBoxes.map((b) => b.maxY)),
        width: 0,
        height: 0,
      }
    : { minX: 0, minY: 0, maxX: 100, maxY: 100, width: 100, height: 100 };
  bounds.width = bounds.maxX - bounds.minX;
  bounds.height = bounds.maxY - bounds.minY;

  return {
    greenhouses,
    bounds,
    summary: {
      areaHa: greenhouses.reduce((s, g) => s + g.areaM2, 0) / 10_000,
      greenhouses: greenhouses.length,
      sectors: greenhouses.reduce((s, g) => s + g.blocks.length, 0),
      lines: greenhouses.reduce((s, g) => s + g.rows.length, 0),
    },
  };
}

export interface FarmHealthOptions {
  /**
   * CÓMO ESTABA EL MAPA ESE DÍA (`yyyy-MM-dd`). Ausente = hoy, en vivo.
   *
   * Con fecha, el modelo solo puede mirar lo que había pasado hasta ese día:
   * partes, resoluciones, tareas y pulverizaciones posteriores no existen. La
   * identidad —qué plaga es, a qué foco pertenece, dónde se pinta— es «como se
   * sabe hoy»: una corrección de plaga se aplica también al pasado.
   */
  asOf?: string;
}

/** Derivación PURA de la sanidad sobre una colocación ya calculada. `today` = ISO date. */
export function deriveFarmHealth(
  layout: FarmLayout,
  input: FarmHealthInput,
  range: HealthRange,
  today: string,
  opts: FarmHealthOptions = {},
): FarmHealthModel {
  const { greenhouses } = layout;
  /* EL DÍA QUE SE MIRA. Sin `asOf` es hoy y manda el estado de hoy TAL CUAL: ni
     una marca de tiempo decide nada, así que el mapa en vivo es exactamente el
     de siempre. Con `asOf`, un hecho solo cuenta si ya había pasado ese día; si
     no se sabe cuándo pasó, se da por pasado —se degrada al estado de hoy, que
     es lo que se sabe— en vez de inventarle una fecha. */
  const asOf = opts.asOf ?? null;
  const D = asOf ?? today;
  const happenedBy = (ts: string | null | undefined): boolean =>
    asOf == null || (ts ? ts.slice(0, 10) : "0000-00-00") <= asOf;
  const focusById = new Map((input.foci ?? []).map((f) => [f.id, f]));
  /* Quién es quién entre las plagas, y en qué estado está cada tarea. Los dos
     se resuelven UNA vez para todo el lote. */
  const canon = buildPestCanon(input.pests ?? []);
  const taskById = new Map((input.tasks ?? []).map((t) => [t.id, t]));

  const ghByBlock = new Map<string, GhModel>();
  const blockById = new Map<string, GhBlock>();
  const rowById = new Map<string, GhRow>();
  const rowByBlockLine = new Map<string, GhRow>();
  for (const gh of greenhouses) {
    for (const b of gh.blocks) { ghByBlock.set(b.id, gh); blockById.set(b.id, b); }
    for (const r of gh.rows) { rowById.set(r.id, r); rowByBlockLine.set(`${r.blockId}:${r.lineNumber}`, r); }
  }

  // ── Tratamientos por bloque (último + PHI derivado) ───────────────────
  const lastSprayByBlock = new Map<string, { date: string; product: string | null; phiUntil: string | null }>();
  const phiByBlock = new Map<string, { until: string; product: string }>();
  const spraysByBlock = new Map<string, RawSpray[]>();
  for (const s of input.sprays) {
    if (!s.block_id || !blockById.has(s.block_id)) continue;
    if (asOf != null && s.date > asOf) continue;
    const arr = spraysByBlock.get(s.block_id) ?? [];
    arr.push(s);
    spraysByBlock.set(s.block_id, arr);
    const product = s.spray_log_entries.find((e) => e.product_name_snapshot)?.product_name_snapshot ?? null;
    const maxPhi = Math.max(0, ...s.spray_log_entries.map((e) => e.phi_days_applied ?? 0));
    const phiUntil = maxPhi > 0 ? addDays(s.date, maxPhi) : null;
    const prev = lastSprayByBlock.get(s.block_id);
    if (!prev || s.date > prev.date) lastSprayByBlock.set(s.block_id, { date: s.date, product, phiUntil });
    if (phiUntil && phiUntil >= D) {
      const prevPhi = phiByBlock.get(s.block_id);
      if (!prevPhi || phiUntil > prevPhi.until) phiByBlock.set(s.block_id, { until: phiUntil, product: product ?? "—" });
    }
  }

  // ── Sesiones → estado + ancla legacy ──────────────────────────────────
  const obsBySession = new Map<string, RawObservation[]>();
  for (const o of input.observations) {
    const arr = obsBySession.get(o.scouting_session_id) ?? [];
    arr.push(o);
    obsBySession.set(o.scouting_session_id, arr);
  }

  interface Anchored {
    session: RawSession;
    state: LineState;
    rowId: string | null;
    blockId: string;
    point: XY;
    approx: boolean;
    accuracyM: number | null;
    pestName: string | null;
    /** La clave canónica: es la que agrupa, no el nombre escrito. */
    pestKey: string | null;
    severity: string | null;
    sprayedNearby: Focus["sprayedNearby"];
    /** El foco al que pertenece de verdad (confirmado y enlazado), o null. */
    focusId: string | null;
    kind: FocusSample["kind"];
    inRange: boolean;
  }
  const anchored: Anchored[] = [];
  for (const s of input.sessions) {
    if (!s.block_id || !blockById.has(s.block_id)) continue;
    if (asOf != null && s.date > asOf) continue; // ese día este parte no existía
    /* SOLO LO CONFIRMADO PERTENECE A UN FOCO. Un `focus_id` en un parte que ya no
       está clasificado es un resto (un bundle viejo le quitó la clasificación):
       no cuenta. La única excepción es la revisión limpia, que es un descartado
       con motivo propio y SÍ forma parte de la historia del foco. */
    const focusRow = s.focus_id ? focusById.get(s.focus_id) ?? null : null;
    const isCleanCheck = focusRow != null && s.triage_status === "dismissed" && s.dismissal_reason === "clean_check";
    if (s.triage_status === "dismissed" && !isCleanCheck) continue;
    const isMember = focusRow != null && (s.triage_status === "classified" || isCleanCheck);
    const isResolved = s.review_status === "resolved" && happenedBy(s.resolved_at ?? s.triaged_at ?? s.date);
    const inRange = s.date >= range.from && s.date <= range.to;
    const isActive = !isResolved;
    // Un miembro entra siempre: si su foco se pinta, se pinta con toda su historia.
    if (!isMember && !inRange && !isActive) continue;

    // Línea ANTES del estado: un spray con rango solo cubre sus líneas.
    let row: GhRow | null = s.block_row_id ? rowById.get(s.block_row_id) ?? null : null;
    if (!row) {
      const legacyLine = parseLegacyLine(s.notes) ?? obsBySession.get(s.id)?.find((o) => o.observed_row_start != null)?.observed_row_start ?? null;
      if (legacyLine != null) row = rowByBlockLine.get(`${s.block_id}:${legacyLine}`) ?? null;
    }
    const lineNo = row?.lineNumber ?? null;
    const coversLine = (sp: RawSpray) =>
      sp.row_start == null || sp.row_end == null
        ? true // sin rango = bloque entero
        : lineNo != null && lineNo >= sp.row_start && lineNo <= sp.row_end;

    /* QUÉ PLAGA ES, canonizada. Va antes que el estado porque decide si una
       pulverización habla de este parte o de otra cosa. */
    const obsPest = obsBySession.get(s.id)?.find((o) => o.pest_name_snapshot)?.pest_name_snapshot ?? null;
    const pestName = s.triage_pest_name_snapshot ?? obsPest;
    const pestKey = canon.key(s.triage_pest_id, pestName);

    /* LA PULVERIZACIÓN DE LA MISMA PLAGA, si la hay. Cuatro condiciones y las
       cuatro hacen falta:
         · después del parte —tratar antes no lo explica—,
         · dentro de la ventana —sin ella, un parte de enero se daba por tratado
           con una aplicación de agosto y ya no volvía nunca—,
         · cubriendo la línea si la aplicación llevaba rango,
         · y PARA ESTA PLAGA, que es la que faltaba: sin ella, en una finca que
           pulveriza por calendario la ventana siempre contiene algo.
       Se queda la MÁS RECIENTE: es la que manda para atenuar el rojo. */
    const nearby = ((): Focus["sprayedNearby"] => {
      if (pestKey == null) return null;
      let best: Focus["sprayedNearby"] = null;
      for (const sp of spraysByBlock.get(s.block_id) ?? []) {
        if (sp.date < s.date) continue;
        if (daysBetween(s.date, sp.date) > TREATED_BLOCK_DAYS) continue;
        if (!coversLine(sp)) continue;
        const hit = sp.spray_log_entries.find((e) =>
          samePest(canon.key(e.pest_id, e.pest_name_snapshot), pestKey),
        );
        if (!hit) continue;
        if (!best || sp.date > best.date) {
          best = {
            sprayId: sp.id,
            date: sp.date,
            product: hit.product_name_snapshot ?? null,
            daysAgo: daysBetween(sp.date, D),
          };
        }
      }
      return best;
    })();

    /* EL ESTADO SALE DE LA TAREA, no de la revisión del manager. `in_review` /
       `in_progress` son de la SESIÓN y decían «en tratamiento» aunque la tarea
       estuviera ya completada o ni siquiera empezada.
       Y una pulverización no enlazada NO entra aquí: por muy de la misma plaga
       que sea, nadie ha cerrado este parte. Vive en `nearby`. */
    const task = s.linked_task_id ? taskById.get(s.linked_task_id) ?? null : null;
    let state: LineState;
    if (isResolved) state = "resolved";
    // El trabajador lo da por resuelto y falta que lo valides: hecho está.
    else if (s.worker_resolved_at != null && happenedBy(s.worker_resolved_at)) state = "treated";
    else if (task?.status === "completed" && happenedBy(task.completed_at)) state = "treated";
    // Plan anulado = vuelve a no haber nada. No se queda en azul.
    else if (task?.status === "cancelled") state = "open";
    /* Hay enlace pero no sabemos su estado (tarea de otro lote o borrada):
       previsto. Algo se decidió aquí, aunque no sepamos qué fue de ello.
       Mirando al pasado: previsto solo si la tarea ya existía ese día. */
    else if (s.linked_task_id && (task == null || happenedBy(task.created_at))) state = "planned";
    else state = "open";

    // Ancla: la REAL (T-B, gps_snap/line_midpoint) si existe; legacy ya
    // resuelto arriba (row).
    const block = blockById.get(s.block_id)!;
    const ax = s.anchor_x != null ? Number(s.anchor_x) : null;
    const ay = s.anchor_y != null ? Number(s.anchor_y) : null;
    const hasRealAnchor = ax != null && ay != null;
    anchored.push({
      session: s,
      state,
      rowId: row?.id ?? null,
      blockId: s.block_id,
      point: hasRealAnchor
        ? { x: ax, y: ay }
        : row
          ? { x: (row.a.x + row.b.x) / 2, y: (row.a.y + row.b.y) / 2 }
          : block.centroid,
      // Aproximado = la posición NO está verificada por GPS dentro de la
      // nave. Solo gps_snap es preciso; line_midpoint (sin fix o con GPS
      // lejos de la nave, "line_midpoint_gps_far") y block_centroid son
      // estimaciones (fix adversarial 2026-08-04: antes line_midpoint se
      // pintaba idéntico a un GPS verificado).
      approx: hasRealAnchor ? !PRECISE_ANCHOR_SOURCES.has(s.anchor_source ?? "") : !row,
      accuracyM: s.gps_accuracy_m ?? null,
      pestName,
      pestKey,
      severity: s.severity,
      sprayedNearby: nearby,
      focusId: isMember ? focusRow!.id : null,
      kind: isCleanCheck ? "clean_check" : "sampling",
      inRange,
    });
  }

  /* ── UN PARTE, UN FOCO ────────────────────────────────────────────────
     Se agrupaban por `línea|bloque + plaga` y el pin llevaba el recuento. Tres
     partes en un sector eran un punto con un «3», y de ahí no se salía: tocarlo
     abría SOLO la última sesión, así que los otros dos no se podían leer desde
     el mapa (petición del usuario 31-08-2026: «si hay tres partes, quiero tres
     chinchetas»).
     Además la fusión mentía por otro lado: `worseState` se quedaba con el peor
     estado del grupo, así que un parte ya tratado desaparecía debajo de dos sin
     tratar — justo lo que pasa hoy en Nave 4 sector A.
     Ahora se apilan al dibujar, no al calcular: ver `fociLayout`. */
  // Lo que dijo quien muestreó, con la misma regla que la ficha del muestreo («No lo sé» incluido).
  const verdictOf = (s: RawSession): FocusEvolution | null => saidVerdict(s);
  const sampleOf = (a: Anchored): FocusSample => ({
    sessionId: a.session.id,
    kind: a.kind,
    date: a.session.date,
    state: a.state,
    severity: a.severity,
    verdict: verdictOf(a.session),
    approx: a.approx,
  });
  const byAge = (a: Anchored, b: Anchored): number => {
    if (a.session.date !== b.session.date) return a.session.date < b.session.date ? -1 : 1;
    const ca = a.session.created_at ?? "";
    const cb = b.session.created_at ?? "";
    if (ca !== cb) return ca < cb ? -1 : 1;
    return a.session.id < b.session.id ? -1 : a.session.id > b.session.id ? 1 : 0;
  };

  const foci: Focus[] = [];
  const members = new Map<string, Anchored[]>();
  for (const a of anchored) {
    if (a.focusId) {
      const arr = members.get(a.focusId);
      if (arr) arr.push(a);
      else members.set(a.focusId, [a]);
      continue;
    }
    // Suelto o sin enlazar: él solo, exactamente como antes de que hubiera focos.
    foci.push({
      id: a.session.id,
      ghId: ghByBlock.get(a.blockId)!.id,
      blockId: a.blockId,
      rowId: a.rowId,
      x: a.point.x,
      y: a.point.y,
      pestName: a.pestName,
      pestKey: a.pestKey,
      severity: a.severity,
      date: a.session.date,
      state: a.state,
      approx: a.approx,
      accuracyM: a.accuracyM,
      sessionIds: [a.session.id],
      sprayedNearby: a.sprayedNearby,
      kind: a.session.triage_status === "pending_triage" ? "loose" : "implicit",
      focusId: null,
      count: 1,
      firstDate: a.session.date,
      samples: [sampleOf(a)],
      evolution: deriveEvolution(null, { severity: a.severity, verdict: verdictOf(a.session) }),
      rebound: false,
    });
  }

  /* ── UN FOCO, UNA CHINCHETA (decisión del usuario, 21-09-2026) ────────────
     Enmienda el «un parte, un foco» del 31-08, y conserva lo que aquel arregló:
      · ningún parte queda fuera de alcance — van todos en `samples` y la ficha
        los abre uno a uno;
      · un muestreo tratado no desaparece debajo de otro sin tratar — MANDA EL
        MÁS RECIENTE, no el peor. `worseState` no entra aquí.
     El sitio es el del primer muestreo MEDIDO (si no, el primero): no depende de
     la fecha que se mire, así que la barra del histórico no mueve la chincheta. */
  for (const [focusId, all] of members) {
    const sorted = [...all].sort(byAge);
    const samplings = sorted.filter((a) => a.kind === "sampling");
    if (samplings.length === 0) continue; // solo revisiones limpias: no hay plaga que pintar
    const latest = samplings[samplings.length - 1];
    if (latest.state === "resolved" && !sorted.some((a) => a.inRange)) continue;
    const anchor = samplings.find((a) => a.session.anchor_source === "gps_snap") ?? samplings[0];
    const row = focusById.get(focusId)!;
    const last = sorted[sorted.length - 1];
    const prev = samplings.length > 1 ? samplings[samplings.length - 2] : null;
    const stateCounts: Partial<Record<LineState, number>> = {};
    for (const a of samplings) stateCounts[a.state] = (stateCounts[a.state] ?? 0) + 1;
    const pestName = row.pest_name_snapshot ?? latest.pestName;
    foci.push({
      id: focusId,
      ghId: ghByBlock.get(anchor.blockId)!.id,
      blockId: anchor.blockId,
      rowId: anchor.rowId,
      x: anchor.point.x,
      y: anchor.point.y,
      pestName,
      pestKey: canon.key(row.pest_id, pestName) ?? latest.pestKey,
      severity: latest.severity,
      date: latest.session.date,
      state: latest.state,
      approx: anchor.approx,
      accuracyM: anchor.accuracyM,
      sessionIds: sorted.map((a) => a.session.id),
      sprayedNearby: latest.sprayedNearby,
      kind: "focus",
      focusId,
      count: samplings.length,
      firstDate: sorted[0].session.date,
      samples: sorted.map(sampleOf),
      // Una revisión limpia posterior al último muestreo dice «ya no hay», diga lo que diga la gravedad.
      evolution:
        last.kind === "clean_check"
          ? "gone"
          : deriveEvolution(prev ? { severity: prev.severity } : null, { severity: latest.severity, verdict: verdictOf(latest.session) }),
      rebound: (row.reopen_count ?? 0) > 0 && row.last_reopened_at != null && happenedBy(row.last_reopened_at),
      stateCounts,
    });
  }
  // Orden estable: el mismo lote pinta siempre igual, venga como venga de la consulta.
  foci.sort((a, b) => (a.date !== b.date ? (a.date < b.date ? 1 : -1) : a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  /* ── Cuántos partes hay de cada plaga ──────────────────────────────────
     El RECUENTO se calcula aquí, ahora que cada parte es su propio foco. Antes
     venía de `Focus.reports` porque el foco era el grupo; hoy el foco es un
     parte y quien quiera contar, cuenta. */
  const aggregatePests = (fs: Focus[]): PestAgg[] => {
    const by = new Map<string, PestAgg>();
    for (const f of fs) {
      const k = f.pestKey ?? f.pestName ?? "?";
      const prev = by.get(k);
      if (!prev) {
        by.set(k, { pestName: f.pestName, severity: f.severity, lastDate: f.date, reports: f.count ?? 1 });
        continue;
      }
      prev.reports += f.count ?? 1;
      if (f.date > prev.lastDate) prev.lastDate = f.date;
      if ((SEV_RANK[f.severity ?? ""] ?? 0) > (SEV_RANK[prev.severity ?? ""] ?? 0)) prev.severity = f.severity;
    }
    // Más partes primero; a igualdad, el más reciente.
    return [...by.values()].sort((a, b) => b.reports - a.reports || (a.lastDate < b.lastDate ? 1 : -1));
  };

  // ── Salud por línea ───────────────────────────────────────────────────
  const fociByRow = new Map<string, Focus[]>();
  for (const f of foci) {
    if (!f.rowId) continue;
    const arr = fociByRow.get(f.rowId);
    if (arr) arr.push(f);
    else fociByRow.set(f.rowId, [f]);
  }
  const lineHealth = new Map<string, LineHealth>();
  for (const [rowId, fs] of fociByRow) {
    /* La línea es UNA, así que se queda con lo peor de sus partes: el estado
       más urgente, la severidad mayor y la fecha más fresca. El número de
       partes de la línea entra en el peso igual que antes entraba el recuento
       del foco agrupado. */
    let state: LineState = "no_data";
    let sevRank = 0;
    let sev = 0.45;
    let last = fs[0].date;
    for (const f of fs) {
      state = worseState(state, f.state);
      if (f.date > last) last = f.date;
      const r = SEV_RANK[f.severity ?? ""] ?? 0;
      if (r > sevRank) { sevRank = r; sev = SEV_WEIGHT[f.severity ?? ""] ?? 0.45; }
    }
    if (sevRank === 0) sev = SEV_WEIGHT[fs[0].severity ?? ""] ?? 0.45;
    const fresh = Math.max(0.15, Math.pow(0.5, daysBetween(last, D) / 7));
    const reports = fs.reduce((n, f) => n + (f.count ?? 1), 0);
    const rep = Math.min(1.6, 1 + 0.25 * Math.log(Math.max(1, reports)));
    lineHealth.set(rowId, {
      state,
      weight: Math.min(1, sev * fresh * rep),
      pests: aggregatePests(fs),
    });
  }

  // ── Salud por bloque ──────────────────────────────────────────────────
  const irmByBlock = new Map<string, { status: "warning" | "over"; detail: string }>();
  // El aviso de resistencias es una foto de HOY: mirando al pasado no se enseña.
  for (const r of asOf != null && asOf < today ? [] : input.irm) {
    if (r.status !== "warning" && r.status !== "over") continue;
    const prev = irmByBlock.get(r.block_id);
    const detail = `${r.product_name} ${r.applications_used}/${r.max_applications}`;
    if (!prev || (prev.status === "warning" && r.status === "over")) {
      irmByBlock.set(r.block_id, { status: r.status, detail });
    }
  }
  const blockHealth = new Map<string, BlockHealth>();
  for (const gh of greenhouses) {
    for (const b of gh.blocks) {
      const rows = gh.rows.filter((r) => r.blockId === b.id);
      const affected = rows.filter((r) => lineHealth.has(r.id));
      const blockFoci = foci.filter((f) => f.blockId === b.id);
      let state: LineState = "no_data";
      let pressure = 0;
      for (const f of blockFoci) {
        state = worseState(state, f.state);
        const lh = f.rowId ? lineHealth.get(f.rowId) : null;
        pressure = Math.max(pressure, lh?.weight ?? 0.4);
      }
      /* La dominante es la que MÁS PARTES tiene, que es lo que siempre quiso
         decir. Con los focos agrupados se comparaba `reports` entre grupos y
         funcionaba de rebote; con un parte por foco había que contar de verdad
         o habría ganado siempre el primero de la lista. */
      const dominant: PestAgg | null = aggregatePests(blockFoci)[0] ?? null;
      const last = lastSprayByBlock.get(b.id) ?? null;
      /* Solo cuenta dentro de la ventana: la consulta pide 45 días de más para
         los plazos de seguridad, y un sector tratado en junio no está verde
         hoy. La misma ventana que usa `sprayedNearby`, para que el sector y sus
         partes no cuenten los días de forma distinta. */
      const treatedDaysAgo =
        last && daysBetween(last.date, D) <= TREATED_BLOCK_DAYS
          ? daysBetween(last.date, D)
          : null;
      blockHealth.set(b.id, {
        totalLines: rows.length,
        affectedLines: affected.length,
        state,
        pressure,
        dominantPest: dominant,
        phi: phiByBlock.get(b.id) ?? null,
        irm: irmByBlock.get(b.id) ?? null,
        lastTreatment: last ? { product: last.product ?? "—", date: last.date, phiUntil: last.phiUntil } : null,
        treatedDaysAgo,
      });
    }
  }

  /* ── Las pulverizaciones, como chinchetas ──────────────────────────────
     TODAS las del rango, no solo las que explican un parte: con el cruce por
     plaga puesto, solo 3 de las ~190 recientes explican algo, y un mapa de
     tratamientos con tres puntos no cuenta lo que pasa en la finca. Las que sí
     explican se dibujan enteras y las de calendario más discretas.
     El rango se aplica AQUÍ y no en la consulta: la consulta pide 45 días de
     más para poder calcular plazos de seguridad, y esos no se pintan. */
  const explainedSprayIds = new Set(
    foci.map((f) => f.sprayedNearby?.sprayId).filter(Boolean) as string[],
  );
  const treatments: TreatmentMark[] = [];
  for (const sp of input.sprays) {
    if (!sp.block_id) continue;
    const block = blockById.get(sp.block_id);
    const gh = ghByBlock.get(sp.block_id);
    if (!block || !gh) continue;
    if (sp.date < range.from || sp.date > range.to) continue;
    if (asOf != null && sp.date > asOf) continue;
    const products = [
      ...new Set(sp.spray_log_entries.map((e) => e.product_name_snapshot).filter(Boolean) as string[]),
    ];
    const pests = [
      ...new Set(sp.spray_log_entries.map((e) => e.pest_name_snapshot).filter(Boolean) as string[]),
    ];
    const pestKeys = [
      ...new Set(
        sp.spray_log_entries
          .map((e) => canon.key(e.pest_id, e.pest_name_snapshot))
          .filter(Boolean) as string[],
      ),
    ];
    treatments.push({
      id: sp.id,
      ghId: gh.id,
      blockId: sp.block_id,
      x: block.centroid.x,
      y: block.centroid.y,
      date: sp.date,
      daysAgo: daysBetween(sp.date, D),
      products,
      pests,
      pestKeys,
      taskId: sp.task_id ?? null,
      explains: explainedSprayIds.has(sp.id),
    });
  }

  // ── Stats por nave + resumen + bounds ─────────────────────────────────
  const ghStats = new Map<string, GhStats>();
  for (const gh of greenhouses) {
    const ghFoci = foci.filter((f) => f.ghId === gh.id && f.state !== "resolved");
    let worst: string | null = null;
    for (const f of ghFoci) {
      if ((SEV_RANK[f.severity ?? ""] ?? 0) > (SEV_RANK[worst ?? ""] ?? 0)) worst = f.severity;
    }
    ghStats.set(gh.id, {
      sectors: gh.blocks.length,
      lines: gh.rows.length,
      activeFoci: ghFoci.length,
      worstSeverity: worst,
      blocksInPhi: gh.blocks.filter((b) => phiByBlock.has(b.id)).length,
    });
  }
  return {
    bounds: layout.bounds,
    greenhouses,
    foci,
    lineHealth,
    blockHealth,
    treatments,
    ghStats,
    summary: layout.summary,
  };
}

/** Derivación PURA del modelo (testeable sin red). `today` = ISO date. */
/**
 * Anclas EXACTAS: la chincheta no se pinta «aproximada» (sin guiones). `gps_snap`
 * es una posición medida dentro de la nave; `manual_map` la puso una persona
 * con el dedo sobre el plano (22-09-2026, Plan 3). Las demás (`line_midpoint`,
 * `line_midpoint_gps_far`, `block_centroid`, `focus_anchor`) son estimaciones.
 * OJO: esto es solo para PINTAR; la regla de enlace F2 («misma línea a más de
 * 30 m con las DOS posiciones medidas») usa `PRECISE_SOURCE` de `focusLinking`
 * y NO se toca: un muestreo manual nunca pasa por `proposeLink`.
 */
const PRECISE_ANCHOR_SOURCES: ReadonlySet<string> = new Set(["gps_snap", "manual_map"]);

export function buildFarmHealthModel(
  input: FarmHealthInput,
  range: HealthRange,
  today: string,
  opts: FarmHealthOptions = {},
): FarmHealthModel {
  return deriveFarmHealth(buildFarmLayout(input), input, range, today, opts);
}

export { SEV_RANK, SEV_WEIGHT }; // shared with src/lib/reports/constants.ts (tests assert equality)
